import { Dispatch, SetStateAction } from 'react';
import {
  Box,
  FormControl,
  Input,
  Modal,
  ModalContent,
  ModalOverlay,
} from '@chakra-ui/react';
import { Button, Text } from '@mercator-gcp/grout';
import { useForm } from 'react-hook-form';
import { CrossIcon } from 'components/icons/CrossIcon';
import { FlexColumn } from 'components/layout/FlexColumn/FlexColumn';
import { FlexRow } from 'components/layout/FlexRow';
import { useForgotPassword } from 'hooks/useForgotPassword';

const SUBMIT_BUTTON_TEXT = 'Submit';

type ForgotPasswordProps = {
  IsPasswordModalOpen: boolean;
  setIsPasswordModalOpen: Dispatch<SetStateAction<boolean>>;
  onCancelClick?: () => void;
  onSaveClick?: (clickText: string) => void;
};

interface FormData {
  email: string;
}

const ForgotPassword = ({
  IsPasswordModalOpen,
  setIsPasswordModalOpen,
  onSaveClick,
}: ForgotPasswordProps) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormData>();
  const { mutate, isLoading, isSuccess, reset: resetMutationState } = useForgotPassword();

  const onSubmit = (data: FormData) => {
    mutate(data.email, {
      onSuccess: () => {
        reset(); // Reset form fields
      },
    });
    onSaveClick?.(SUBMIT_BUTTON_TEXT);
  };

  const handleCloseModal = () => {
    setIsPasswordModalOpen(false);
    reset(); // Reset form fields when modal closes
    resetMutationState(); // Reset mutation state
  };

  const centerFlex = {
    justifyContent: 'center',
    width: '100%',
  };

  const spaceBetweenFlex = {
    justifyContent: 'space-between',
  };

  const closeButtonConfirm = {
    mt: 'padding_32',
  };

  const iconContainer = { cursor: 'pointer' };

  const modalContent = { maxWidth: '521px', width: '100%', padding: 'padding_24' };

  const formContainer = { width: '100%', gap: 'padding_16' };

  const formBody = { gap: 'padding_8', mb: 'padding_16' };

  const inputContainer = { width: '100%', marginBottom: '24px' };

  return (
    <Modal isOpen={IsPasswordModalOpen} onClose={handleCloseModal}>
      <ModalOverlay />
      <ModalContent sx={modalContent}>
        <FlexColumn>
          {isSuccess && (
            <FlexColumn>
              <Text.Lead>Password request sent!</Text.Lead>
              <Text>
                We will send instructions to access your password as soon as possible.
              </Text>

              <FlexRow sx={{ ...centerFlex, ...closeButtonConfirm }}>
                <Button
                  onClick={() => {
                    handleCloseModal();
                  }}
                  variant="primary"
                >
                  Close
                </Button>
              </FlexRow>
            </FlexColumn>
          )}
          {!isSuccess && (
            <form onSubmit={handleSubmit(onSubmit)}>
              <FlexColumn sx={formContainer}>
                <FlexRow sx={spaceBetweenFlex}>
                  <Text.Lead>Reset Password</Text.Lead>
                  <Box sx={iconContainer} onClick={handleCloseModal}>
                    <CrossIcon stroke="#000000" />
                  </Box>
                </FlexRow>
                <FlexColumn sx={formBody}>
                  <Text>
                    Enter your email to reset your password. Your new password will be
                    sent to your inbox.
                  </Text>
                </FlexColumn>
                <FormControl sx={inputContainer}>
                  <Text>Email</Text>
                  <Input type="email" {...register('email', { required: true })} />
                  {errors.email && <Text>This field is required</Text>}
                </FormControl>
                <FlexRow sx={spaceBetweenFlex}>
                  <Button variant="functional" onClick={handleCloseModal}>
                    Cancel
                  </Button>
                  <Button type="submit" variant="primary" isLoading={isLoading}>
                    {SUBMIT_BUTTON_TEXT}
                  </Button>
                </FlexRow>
              </FlexColumn>
            </form>
          )}
        </FlexColumn>
      </ModalContent>
    </Modal>
  );
};

export default ForgotPassword;
